import React, { Component } from 'react';
import { arrayOf, func, node, number, shape, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { formatCurrencyMajorUnit } from '../../util/currency';
import config from '../../config';

import { CustomFilterForm } from '../../forms';

import css from './CustomFilterPlain.module.css';
import { fetchAge, normalizeAge } from '../../util/urlHelpers';

const RADIX = 10;

const getPriceQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'price';
};

// Parse value, which should look like "0,1000"
const parse = priceRange => {
  const [minAge, maxAge] = !!priceRange
    ? priceRange.split(',').map(v => Number.parseInt(v, RADIX))
    : [];
  // Note: we compare to null, because 0 as minAge is falsy in comparisons.
  return !!priceRange && minAge != null && maxAge != null ? { minAge, maxAge } : null;
};

// Format value, which should look like { minAge, maxAge }
const format = (range, queryParamName) => {
  const { minAge, maxAge } = range || {};
  // Note: we compare to null, because 0 as minAge is falsy in comparisons.
  const value = minAge != null && maxAge != null ? `${minAge},${maxAge}` : null;
  return { [queryParamName]: value };
};

class CustomFilterPlainComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { isOpen: true };

    this.handleChange = this.handleChange.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.toggleIsOpen = this.toggleIsOpen.bind(this);
  }

  handleChange(values) {
    const { onSubmit, queryParamNames } = this.props;
    // const minAgeYear = fetchAge(values?.minAge);
    // const maxAgeYear = fetchAge(values?.maxAge);
    const minAgeYear = fetchAge(values?.minAge) + 1;
    const maxAgeYear = fetchAge(values?.maxAge) - 1;
    const finalValues = {
      maxAge: minAgeYear,
      minAge: maxAgeYear,
    };
    const priceQueryParamName = getPriceQueryParamName(queryParamNames);
    onSubmit(format(finalValues, priceQueryParamName));
  }

  handleClear() {
    const { onSubmit, queryParamNames } = this.props;
    const priceQueryParamName = getPriceQueryParamName(queryParamNames);
    onSubmit(format(null, priceQueryParamName));
  }

  toggleIsOpen() {
    this.setState(prevState => ({ isOpen: !prevState.isOpen }));
  }

  render() {
    const {
      rootClassName,
      className,
      id,
      label,
      queryParamNames,
      initialValues,
      min,
      max,
      step,
      intl,
      currencyConfig,
    } = this.props;
    const classes = classNames(rootClassName || css.root, className);

    const priceQueryParam = getPriceQueryParamName(queryParamNames);
    const initialPrice = initialValues ? parse(initialValues[priceQueryParam]) : {};
    const { minAge, maxAge } = initialPrice || {};
    // const minAgeFinal = normalizeAge(maxAge);
    // const maxAgeFinal = normalizeAge(minAge);
    const minAgeFinal = normalizeAge(maxAge) + 1;
    const maxAgeFinal = normalizeAge(minAge) - 1;
    const hasValue = value => value != null;
    const hasInitialValues =
      initialValues && hasValue(minAgeFinal || minAge) && hasValue(maxAgeFinal || maxAge);

    const labelClass = hasInitialValues ? css.filterLabelSelected : css.filterLabel;
    const labelText = hasInitialValues
      ? intl.formatMessage(
          { id: 'PriceFilter.labelSelectedPlain' },
          {
            minPrice: formatCurrencyMajorUnit(intl, null, minAgeFinal || minAge),
            maxPrice: formatCurrencyMajorUnit(intl, null, maxAgeFinal || maxAge),
          }
        )
      : label
      ? label
      : intl.formatMessage({ id: 'PriceFilter.label' });

    return (
      <div className={classes}>
        <div className={labelClass}>
          <button type="button" className={css.labelButton} onClick={this.toggleIsOpen}>
            <span className={labelClass}>{labelText}</span>
          </button>
          <button type="button" className={css.clearButton} onClick={this.handleClear}>
            <FormattedMessage id={'PriceFilter.clear'} />
          </button>
        </div>
        <div className={css.formWrapper}>
          <CustomFilterForm
            id={id}
            initialValues={
              hasInitialValues
                ? {
                    minAge: normalizeAge(initialPrice?.maxAge) + 1,
                    maxAge: normalizeAge(initialPrice?.minAge) - 1,
                  }
                : { minAge: min, maxAge: max }
            }
            onChange={this.handleChange}
            intl={intl}
            contentRef={node => {
              this.filterContent = node;
            }}
            min={min}
            max={max}
            step={step}
            liveEdit
            isOpen={this.state.isOpen}
          />
        </div>
      </div>
    );
  }
}

CustomFilterPlainComponent.defaultProps = {
  rootClassName: null,
  className: null,
  initialValues: null,
  step: number,
  currencyConfig: config.currencyConfig,
};

CustomFilterPlainComponent.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  label: node,
  queryParamNames: arrayOf(string).isRequired,
  onSubmit: func.isRequired,
  initialValues: shape({
    price: string,
  }),
  min: number.isRequired,
  max: number.isRequired,
  step: number,
  currencyConfig: propTypes.currencyConfig,

  // form injectIntl
  intl: intlShape.isRequired,
};

const CustomFilterPlain = injectIntl(CustomFilterPlainComponent);

export default CustomFilterPlain;
